<template>
    <UiModal ref="modal" v-model:display="display" size="lg" body-class="modal-body-profile">
        <template #title>
            <template v-if="loading">{{ t("Loading profile") }}...</template>
            <template v-else-if="notFound">{{ t("User not found") }}</template>
            <template v-else>{{ name || username }} (@{{ username }})</template>
        </template>
        <ComponentLoader v-if="loading"></ComponentLoader>
        <UserProfileView
            v-else-if="!notFound"
            :name="name"
            :username="username"
            :join-date="joinDate"
            :bio="bio"
            :image="image"
            :location="location"
            :website="website"
        />
        <article v-else class="user-profile-not-found">
            <header class="user-profile-not-found-header">
                <figure class="user-profile-not-found-image">
                    <img class="usr-image" src="@/assets/user.png" :alt="t('User not found')" />
                </figure>
                <div class="user-profile-not-found-details">
                    <h3 class="user-profile-not-found-name">{{ t("User not found") }}</h3>
                    <p class="user-profile-not-found-error">
                        {{ t("The user you are looking for does not exist or was deleted from the platform.") }}
                    </p>
                </div>
            </header>
        </article>
        <template v-if="!loading && notFound" #footer="{ close }">
            <UiButton variant="secondary" @click="close">{{ t("Close") }}</UiButton>
        </template>
    </UiModal>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import { Request } from "@asanrom/request-browser";
import { ApiProfile } from "@/api/api-group-profile";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";
import UserProfileView from "@/components/utils/UserProfileView.vue";
import UiModal from "@/components/utils/UiModal.vue";
import UiButton from "@/components/utils/UiButton.vue";
import { useAuthNavigation } from "@/composables/useAuthNavigation";
import { useModalClose } from "@/composables/useModal";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";

defineOptions({ name: "ProfileModal" });

const display = defineModel<boolean>("display");

const { uid } = defineProps<{
    uid?: string;
}>();

const modal = useTemplateRef("modal");
const closeModal = useModalClose(modal);
const { requireLogin } = useAuthNavigation(closeModal);
const loadRequestId = getUniqueStringId();

const loading = ref(true);
const notFound = ref(false);
const username = ref("");
const joinDate = ref("");
const name = ref("");
const bio = ref("");
const image = ref("");
const location = ref("");
const website = ref("");

const load = () => {
    Timeouts.Abort(loadRequestId);
    Request.Abort(loadRequestId);

    loading.value = true;

    Request.Pending(loadRequestId, ApiProfile.GetProfile(uid))
        .onSuccess((profile) => {
            loading.value = false;
            notFound.value = false;
            image.value = profile.image;
            name.value = profile.name;
            username.value = profile.username;
            joinDate.value = profile.joinDate;
            bio.value = profile.bio;
            location.value = profile.location;
            website.value = profile.website;
        })
        .onRequestError((err, handleErr) => {
            handleErr(err, {
                unauthorized: () => {
                    requireLogin();
                },
                notFound: () => {
                    loading.value = false;
                    notFound.value = true;
                },
                temporalError: () => {
                    Timeouts.Set(loadRequestId, 1500, load);
                },
            });
        })
        .onUnexpectedError((err) => {
            console.error(err);
            Timeouts.Set(loadRequestId, 1500, load);
        });
};

watch(
    display,
    (isOpen) => {
        if (isOpen) {
            load();
        }
    },
    { immediate: true },
);

watch(
    () => uid,
    () => {
        load();
    },
);

onBeforeUnmount(() => {
    Timeouts.Abort(loadRequestId);
    Request.Abort(loadRequestId);
});
</script>

<style scoped>
:deep(.modal-body-profile) {
    min-height: 240px;
}

.user-profile-not-found-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
}

.user-profile-not-found-image {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    margin: 0;
    border-radius: 50%;
}

.user-profile-not-found-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    background-color: var(--user-profile-image-background);
}

.user-profile-not-found-name {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
}

.user-profile-not-found-error {
    margin: 0;
    opacity: 0.75;
}

@media (max-width: 640px) {
    .user-profile-not-found-header {
        flex-direction: column;
        text-align: center;
    }
}
</style>
